/**
 * CommonIcon / Custom Icon Component - Lucide React Icon Wrapper
 * 
 * Provides a unified interface for rendering icons from the lucide-react library.
 * Allows dynamic icon selection by name with customizable size and color properties.
 * Includes error handling for missing or invalid icon names.
 * 
 * This wrapper component simplifies icon usage throughout the application by:
 * - Providing a single import point for all icon functionality
 * - Handling dynamic icon resolution by name
 * - Offering sensible defaults (size: 24px, color: currentColor)
 * - Including helpful error logging for debugging
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.name - Name of the lucide-react icon (e.g., "ChevronDown", "Search", "Menu")
 * @param {number} [props.size=24] - Icon size in pixels (default: 24)
 * @param {string} [props.color="currentColor"] - Icon color as CSS value (default: currentColor inherits from parent)
 * @param {Object} props...rest - Additional props passed to the underlying Lucide icon component
 * @returns {JSX.Element|null} Rendered Lucide icon component or null if icon not found
 * 
 * @example
 * // Display a search icon with default size and color
 * <CommonIcon name="Search" />
 * 
 * @example
 * // Display a menu icon with custom size and color
 * <CommonIcon name="Menu" size={32} color="#0d6efd" />
 * 
 * @example
 * // Display a settings icon with additional props (className, onClick, etc.)
 * <CommonIcon name="Settings" size={20} className="cursor-pointer" onClick={handleSettings} />
 * 
 * @requires lucide-react - Icon library providing 400+ SVG icons
 */

import React from 'react';
// Import all icons from lucide-react library
// lucide-react provides a comprehensive set of 400+ customizable SVG icons
import * as Icons from 'lucide-react';

/**
 * Icon rendering component with dynamic icon resolution
 * Wraps lucide-react icons with error handling and prop customization
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.name - Icon name to render (must match lucide-react icon name exactly)
 * @param {number} props.size - Icon size in pixels (default: 24)
 * @param {string} props.color - Icon color as CSS value (default: "currentColor")
 * @param {Object} props - Remaining props are spread to the icon component
 */
const IconComponent = ({ name, size = 24, color = "currentColor", ...props }) => {
  // ========== DYNAMIC ICON RESOLUTION ==========
  // Dynamically retrieve the icon component from lucide-react by name
  // Icons[name] will be undefined if the icon name doesn't exist
  const LucideIcon = Icons[name];

  // ========== ERROR HANDLING ==========
  // Check if the requested icon exists in lucide-react
  if (!LucideIcon) {
    // Log helpful error message to browser console for debugging
    console.error(`Icon "${name}" does not exist in lucide-react.`);
    // Return null to prevent errors from rendering invalid icon component
    return null;
  }

  // ========== RENDER ==========
  // Render the lucide icon component with provided props
  // spread operator (...props) allows passing additional attributes like className, onClick, etc.
  return <LucideIcon size={size} color={color} {...props} />;
};

// Export component as default for use throughout the application
export default IconComponent;
